import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { allWorks, searchWorks } from "@/utils/catalog";
import type { Work, WorkType } from "@/types";
import { usePageMeta } from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";
import WorkCard from "@/components/feature/WorkCard";
import EmptyState from "@/components/feature/EmptyState";

const PAGE_SIZE = 8;

const filters: { key: WorkType | "all"; label: string; icon: string }[] = [
  { key: "all", label: "全部", icon: "ri-apps-2-line" },
  { key: "movie", label: "电影", icon: "ri-film-line" },
  { key: "tv", label: "电视剧", icon: "ri-tv-2-line" },
  { key: "web", label: "网络剧", icon: "ri-smartphone-line" },
];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const query = params.get("q") ?? "";
  const [input, setInput] = useState(query);
  const [typeFilter, setTypeFilter] = useState<WorkType | "all">("all");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setInput(query);
    setPage(1);
  }, [query]);

  usePageMeta({
    title: query ? `搜索“${query}” - 妻子的朋友` : "影视搜索 - 妻子的朋友",
    description: query
      ? `妻子的朋友为你找到与“${query}”相关的影视作品，包含剧情介绍与角色分析。`
      : "在妻子的朋友搜索电影、电视剧与网络剧，快速获取剧情介绍与观影推荐。",
    keywords: "影视搜索,剧情搜索,影视作品,妻子的朋友",
  });

  const baseResults = useMemo(() => (query ? searchWorks(query) : []), [query]);

  const results = useMemo(() => {
    const list = baseResults.filter((w) => typeFilter === "all" || w.type === typeFilter);
    return list;
  }, [baseResults, typeFilter]);

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const shown = results.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const recommend: Work[] = useMemo(
    () => [...allWorks].sort((a, b) => b.rating - a.rating).slice(0, 4),
    []
  );

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const value = input.trim();
    setParams(value ? { q: value } : {});
  };

  const typeCounts = (type: WorkType | "all") =>
    type === "all" ? baseResults.length : baseResults.filter((w) => w.type === type).length;

  return (
    <div className="mx-auto w-full max-w-[1360px] px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb items={[{ label: "搜索" }]} />

      <header className="mt-4">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          {query ? `搜索“${query}”` : "搜索影视作品"}
        </h1>
        <p className="mt-2 text-sm text-foreground-600">
          输入影视名称、导演、演员或题材关键词，快速找到相关剧情介绍。
        </p>

        <form onSubmit={submit} className="mt-5 flex flex-col gap-3 sm:flex-row">
          <div className="flex h-12 flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 text-sm focus-within:border-primary-400">
            <span className="flex h-5 w-5 items-center justify-center text-foreground-400">
              <i className="ri-search-line"></i>
            </span>
            <input
              type="search"
              name="q"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="如：悬疑、暗影之城、陈默…"
              className="h-full w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
          </div>
          <button
            type="submit"
            className="flex h-12 items-center justify-center gap-2 rounded-md bg-primary-500 px-6 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer whitespace-nowrap"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-search-line"></i>
            </span>
            搜索
          </button>
        </form>
      </header>

      {query && (
        <div className="mt-6 flex flex-wrap items-center gap-1 rounded-full border border-background-200 bg-background-50 p-1 w-fit">
          {filters.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => {
                setTypeFilter(f.key);
                setPage(1);
              }}
              className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm transition-colors cursor-pointer whitespace-nowrap ${
                typeFilter === f.key
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-600 hover:text-primary-700"
              }`}
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className={f.icon}></i>
              </span>
              {f.label}
              <span className="text-[11px] opacity-80">{typeCounts(f.key)}</span>
            </button>
          ))}
        </div>
      )}

      <div className="mt-6">
        {!query ? (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-12 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-search-eye-line text-2xl"></i>
            </span>
            <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">
              开始你的影视探索
            </h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-foreground-600">
              输入关键词后即可查看匹配的影视作品。你也可以试试下方的热门关键词。
            </p>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {["悬疑", "古装", "科幻", "爱情", "家庭", "谍战"].map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => {
                    setInput(k);
                    setParams({ q: k });
                  }}
                  className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900 transition-colors hover:bg-secondary-200 cursor-pointer"
                >
                  {k}
                </button>
              ))}
            </div>
          </div>
        ) : shown.length === 0 ? (
          <div className="flex flex-col gap-8">
            <EmptyState
              title={`没有找到与“${query}”相关的内容`}
              description="换个关键词试试，或者参考下方为你智能推荐的高口碑作品。"
            />
            <div>
              <h2 className="mb-4 font-heading text-lg font-bold text-foreground-950">
                也许你会喜欢
              </h2>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                {recommend.map((w, i) => (
                  <WorkCard key={w.id} work={w} index={i} />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            <p className="mb-4 text-sm text-foreground-500">
              找到 <strong className="text-foreground-900">{results.length}</strong> 部相关作品
            </p>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {shown.map((w, i) => (
                <WorkCard key={w.id} work={w} index={i} />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="flex h-9 items-center gap-1 rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-700 transition-colors disabled:cursor-not-allowed disabled:opacity-40 hover:border-primary-300 cursor-pointer whitespace-nowrap"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-arrow-left-s-line"></i>
                  </span>
                  上一页
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPage(p)}
                    className={`flex h-9 w-9 items-center justify-center rounded-md text-sm transition-colors cursor-pointer ${
                      p === page
                        ? "bg-primary-500 text-background-50"
                        : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300"
                    }`}
                  >
                    {p}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="flex h-9 items-center gap-1 rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-700 transition-colors disabled:cursor-not-allowed disabled:opacity-40 hover:border-primary-300 cursor-pointer whitespace-nowrap"
                >
                  下一页
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-arrow-right-s-line"></i>
                  </span>
                </button>
              </div>
            )}

            <div className="mt-10 flex justify-center">
              <Link
                to="/special"
                className="flex h-11 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700 cursor-pointer whitespace-nowrap"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-bookmark-3-line"></i>
                </span>
                浏览专题合集，发现更多好作品
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}